// North Shore service-area pages. Each suburb has genuinely distinct content (local housing stock,
// the locks we find there, the jobs that come up) so these read as real local pages, not
// find-and-replace doorway pages. CommonJS so both React (import) and the Node build script
// (require, via routeMeta.js) can read it — no array spread or destructuring in this file.
//
// slug → URL /service-areas/<slug>; nearby → internal links to adjacent suburbs.
// blurb = intro paragraph; homes = the houses and locks we find there; jobs = common call-outs
// (also feed the page's meta description); services = the four residential services shown on the page.
// isBase marks the suburb we work from (Greenhithe).

const serviceAreas = [
  {
    slug: 'takapuna',
    name: 'Takapuna',
    blurb:
      "Takapuna mixes beachfront apartments, the busy Hurstmere Road retail strip and commercial offices, so we're called out here for everything from apartment lockouts to shopfront and office door hardware. We carry the common residential and commercial locks in the van.",
    homes:
      'Apartments and townhouses near the beach and Lake Pupuke sit alongside older family homes on bigger sections. Apartment entry doors often run commercial-grade mortice locks on restricted keys, which need a proper key system rather than a copy from a kiosk.',
    jobs: [
      'Apartment lockouts and restricted-key copies through the body corporate',
      'Shopfront and office door hardware on Hurstmere Road and The Strand',
      'Deadbolt and digital-lock upgrades for older homes',
    ],
    nearby: ['milford', 'devonport', 'northcote', 'belmont'],
    services: ['lock-rekeying', 'smart-lock-installation', 'deadbolt-installation', 'key-cutting'],
  },
  {
    slug: 'devonport',
    name: 'Devonport',
    blurb:
      "Devonport's older villas and character homes often run original or worn locks that are better rekeyed or sympathetically upgraded than bluntly replaced. We handle heritage-style hardware as readily as modern deadlocks, and we're a quick trip around the waterfront from the rest of the Shore.",
    homes:
      'Victorian and Edwardian villas and bungalows with original timber doors are common, often with rim nightlatches and mortice locks that are decades old. Heritage-style hardware keeps the look while adding a proper deadlock.',
    jobs: [
      'Rekeying and servicing original villa locks',
      'Adding deadlocks without spoiling heritage doors',
      'Shop and café door hardware along Victoria Road',
    ],
    nearby: ['takapuna', 'northcote', 'milford', 'bayswater'],
    services: ['lock-repair-replacement', 'deadbolt-installation', 'lock-rekeying', 'door-handle-repair'],
  },
  {
    slug: 'milford',
    name: 'Milford',
    blurb:
      'Milford runs from the beach and marina up to the Milford Centre, with a mix of family homes, townhouses and apartments. We cover lockouts, rekeying and lock repairs, plus door hardware for the shops and businesses around the centre.',
    homes:
      'Plenty of 1960s and 1970s homes with aluminium joinery sit alongside newer townhouses and apartments. Aluminium ranchslider locks and worn entrance sets are typical repairs here.',
    jobs: [
      'Ranchslider lock repairs on aluminium joinery',
      'Rekeying townhouses and apartments after a move',
      'Door hardware for shops around the Milford Centre',
    ],
    nearby: ['takapuna', 'mairangi-bay', 'albany', 'castor-bay'],
    services: ['sliding-door-locks', 'lock-rekeying', 'lock-repair-replacement', 'door-handle-repair'],
  },
  {
    slug: 'albany',
    name: 'Albany',
    blurb:
      "Albany's newer subdivisions and townhouses tend to run modern euro-cylinder and smart-lock hardware, while its business parks need commercial door hardware and access control. We cover both — and the area's rapid growth means plenty of new fit-outs, rekeys and lock upgrades.",
    homes:
      'Much of Albany was built from the 1990s onwards: subdivisions and townhouses with modern entrance sets, euro-cylinder locks and a growing number of digital locks. The business parks around Rosedale run commercial hardware and access control.',
    jobs: [
      'Rekeying newer homes after settlement',
      'Digital and smart lock installs on modern doors',
      'Commercial door hardware in the Rosedale business parks',
    ],
    nearby: ['glenfield', 'browns-bay', 'mairangi-bay', 'greenhithe'],
    services: ['lock-rekeying', 'smart-lock-installation', 'key-cutting', 'home-security-upgrades'],
  },
  {
    slug: 'glenfield',
    name: 'Glenfield',
    blurb:
      "Glenfield's mix of family homes and light-industrial units keeps us busy with residential rekeys and lock repairs alongside commercial door hardware for the workshops and units off Wairau Road.",
    homes:
      'Glenfield grew quickly in the 1960s and 1970s, so many homes still have their original aluminium joinery and entrance sets, now well worn. Ranchslider locks, drooping handles and tired deadlocks are typical call-outs.',
    jobs: [
      'Ranchslider and aluminium door lock repairs',
      'Door handle and entrance set replacements',
      'Commercial hardware for the units off Wairau Road',
    ],
    nearby: ['birkenhead', 'northcote', 'albany', 'beach-haven', 'greenhithe'],
    services: ['sliding-door-locks', 'door-handle-repair', 'lock-repair-replacement', 'lock-rekeying'],
  },
  {
    slug: 'browns-bay',
    name: 'Browns Bay',
    blurb:
      'Browns Bay and the East Coast Bays are largely coastal residential, where salt air is hard on older locks. We replace seized and corroded hardware, rekey after house moves, and fit home security upgrades for peace of mind by the sea.',
    homes:
      'Coastal homes from the 1960s onwards, many with aluminium joinery facing the sea breeze, plus apartments and shops around the Browns Bay village. Salt air seizes cylinders and corrodes latches faster here than inland.',
    jobs: [
      'Seized and corroded locks replaced with corrosion-resistant hardware',
      'Rekeying after house moves',
      'Shopfront door hardware in the village',
    ],
    nearby: ['mairangi-bay', 'torbay', 'albany', 'rothesay-bay'],
    services: ['lock-repair-replacement', 'lock-rekeying', 'home-security-upgrades', 'sliding-door-locks'],
  },
  {
    slug: 'birkenhead',
    name: 'Birkenhead',
    blurb:
      "Birkenhead and Highbury's older homes and hillside streets see plenty of rekeys, lock repairs and home security upgrades. We come to you across the area with the common hardware already on board.",
    homes:
      'Villas and bungalows on the older streets toward Chelsea, with 1960s and 1970s homes and newer infill up around Highbury. Older timber doors often need a sticking lock eased and the frame adjusted before a new lock will work properly.',
    jobs: [
      'Easing sticking locks and realigning older timber doors',
      'Deadlock upgrades for villas and bungalows',
      'Rekeys for new owners and landlords',
    ],
    nearby: ['northcote', 'glenfield', 'beach-haven'],
    services: ['lock-repair-replacement', 'deadbolt-installation', 'lock-rekeying', 'door-handle-repair'],
  },
  {
    slug: 'northcote',
    name: 'Northcote',
    blurb:
      "Northcote's blend of townhouses, older homes and the town centre means residential lockouts and rekeys plus shopfront and commercial door hardware — all covered by our mobile service.",
    homes:
      "Northcote Point has some of the Shore's oldest villas, while the rest of Northcote mixes state-era homes, townhouses and the new housing around the town centre. That means everything from heritage nightlatches to brand-new digital locks.",
    jobs: [
      'Heritage lock repairs on Northcote Point villas',
      'Rekeying new townhouses after settlement',
      'Shop door hardware around the Northcote town centre',
    ],
    nearby: ['birkenhead', 'takapuna', 'glenfield'],
    services: ['lock-repair-replacement', 'lock-rekeying', 'smart-lock-installation', 'deadbolt-installation'],
  },
  {
    slug: 'mairangi-bay',
    name: 'Mairangi Bay',
    blurb:
      "Mairangi Bay and the surrounding bays are coastal residential. We handle lockouts, rekeying and weather-worn lock replacement, and fit home security upgrades for the area's family homes.",
    homes:
      'Family homes from the 1960s to today on the slopes above the beach, many with ranchsliders opening onto decks. Sliding-door security and window locks are the usual upgrades.',
    jobs: [
      'Sliding door lock repairs and security bolts',
      'Window locks for insurance',
      'Lockouts and rekeys',
    ],
    nearby: ['browns-bay', 'milford', 'torbay', 'campbells-bay'],
    services: ['sliding-door-locks', 'home-security-upgrades', 'lock-rekeying', 'key-cutting'],
  },
  {
    slug: 'torbay',
    name: 'Torbay',
    blurb:
      "Torbay, at the northern end of the Shore, is residential and a little further out. We still come to you for lockouts, rekeys and lock repairs — a quick call ahead helps us plan the run so you're not waiting.",
    homes:
      'Mostly family homes from the 1970s and 1980s on quiet streets near Waiake Beach, with aluminium joinery throughout. Worn ranchslider locks and handle sets are typical.',
    jobs: [
      'Ranchslider lock and handle replacements',
      'Deadbolt upgrades on front doors',
      'Rekeying after a move',
    ],
    nearby: ['browns-bay', 'mairangi-bay', 'long-bay'],
    services: ['sliding-door-locks', 'door-handle-repair', 'deadbolt-installation', 'lock-rekeying'],
  },
  {
    slug: 'beach-haven',
    name: 'Beach Haven',
    blurb:
      'Beach Haven sits out on its own tidal peninsula — a mix of original ex-state homes and newer infill builds, many close enough to the water that salt air works away at older hardware. We come out here for rekeys after settlement, sticking and seized locks, and home security tidy-ups, with the common locks already on the van.',
    homes:
      'Original ex-state homes sit alongside newer infill on the peninsula, and a lot of older timber doors still carry their first locks. Adding a deadlock and rekeying after settlement are the usual first jobs for new owners.',
    jobs: [
      'Rekeying after settlement',
      'Deadlock and window lock upgrades',
      'Sticking and seized locks near the water',
    ],
    nearby: ['birkenhead', 'glenfield', 'greenhithe'],
    services: ['lock-rekeying', 'deadbolt-installation', 'home-security-upgrades', 'lock-repair-replacement'],
  },
  {
    slug: 'bayswater',
    name: 'Bayswater',
    blurb:
      'Bayswater is a quiet marina peninsula of character bungalows and villas between Belmont and Devonport, where the sea air is hard on older locks. We rekey after house moves, replace weather-worn hardware, and fit window and deadlock upgrades.',
    homes:
      'Character bungalows and villas share the peninsula with newer homes near the marina and ferry. Timber doors with older mortice locks are common, and the sea air shortens their life.',
    jobs: [
      'Mortice lock servicing and replacement on older doors',
      'Rekeying after house moves',
      'Window and deadlock upgrades',
    ],
    nearby: ['belmont', 'devonport', 'takapuna'],
    services: ['lock-repair-replacement', 'lock-rekeying', 'home-security-upgrades', 'deadbolt-installation'],
  },
  {
    slug: 'belmont',
    name: 'Belmont',
    blurb:
      'Belmont sits right on Lake Road between Takapuna and Devonport — a settled patch of family homes plus the Belmont shops. We handle lockouts, rekeys after moving in, and lock repairs, along with door hardware for the local shopfronts.',
    homes:
      "Belmont's family homes run from post-war bungalows to newer townhouses along Lake Road, many with original timber front doors and later aluminium sliders.",
    jobs: [
      'Rekeys after moving in',
      'Deadbolt and window lock upgrades',
      'Shopfront door hardware at the Belmont shops',
    ],
    nearby: ['takapuna', 'bayswater', 'devonport'],
    services: ['lock-rekeying', 'deadbolt-installation', 'home-security-upgrades', 'key-cutting'],
  },
  {
    slug: 'forrest-hill',
    name: 'Forrest Hill',
    blurb:
      "Forrest Hill is established inland residential between Takapuna and Sunnynook — post-war homes and steady infill, plus the AUT Millennium end. We're called out for rekeys, worn-lock repairs and home security upgrades, and we come to you.",
    homes:
      'Post-war homes and steady infill, many with original timber doors or 1970s aluminium joinery. Worn entrance sets and ranchslider locks are the usual repairs.',
    jobs: [
      'Entrance set and door handle replacements',
      'Ranchslider lock repairs',
      'Rekeys for new owners',
    ],
    nearby: ['takapuna', 'sunnynook', 'milford'],
    services: ['door-handle-repair', 'sliding-door-locks', 'lock-rekeying', 'lock-repair-replacement'],
  },
  {
    slug: 'sunnynook',
    name: 'Sunnynook',
    blurb:
      'Sunnynook is a family suburb tucked just off the Northern Motorway. Lockouts, rekeying after a move, lock repairs and home security upgrades — all handled on site from the van.',
    homes:
      'Mostly 1970s family homes with aluminium joinery, plus newer townhouses near the busway station. Ranchslider locks and front-door deadbolts are the common upgrades.',
    jobs: [
      'Ranchslider lock repairs',
      'Front-door deadbolt upgrades',
      'Lockouts and rekeys',
    ],
    nearby: ['forrest-hill', 'mairangi-bay', 'milford'],
    services: ['sliding-door-locks', 'deadbolt-installation', 'lock-rekeying', 'key-cutting'],
  },
  {
    slug: 'rothesay-bay',
    name: 'Rothesay Bay',
    blurb:
      "Rothesay Bay is coastal East Coast Bays living, where salt air seizes older locks over time. We replace corroded and sticking hardware, rekey after house moves, and fit home security upgrades for the bays' family homes.",
    homes:
      'Coastal family homes, many with decks and ranchsliders facing the sea. Salt air seizes cylinders and wears hooks, so servicing and corrosion-resistant replacements matter here.',
    jobs: [
      'Seized cylinders freed or replaced',
      'Ranchslider security upgrades',
      'Rekeying after a move',
    ],
    nearby: ['browns-bay', 'mairangi-bay', 'long-bay'],
    services: ['lock-repair-replacement', 'sliding-door-locks', 'lock-rekeying', 'home-security-upgrades'],
  },
  {
    slug: 'long-bay',
    name: 'Long Bay',
    blurb:
      'Long Bay, at the northern end of the Shore by the regional park, is largely newer masterplanned housing running modern euro-cylinder and smart-lock hardware. We cover new-build rekeys, lock upgrades and smart-lock setup — a call ahead helps us plan the run out.',
    homes:
      "Most homes here are recent builds, with modern entrance sets, euro cylinders and a lot of digital locks already fitted. New owners often want the builders' keys cancelled and the house keyed alike.",
    jobs: [
      "Rekeying new builds so the builders' keys stop working",
      'Digital lock set-up and upgrades',
      'Keying the whole house to one key',
    ],
    nearby: ['torbay', 'browns-bay'],
    services: ['lock-rekeying', 'smart-lock-installation', 'key-cutting', 'home-security-upgrades'],
  },
  {
    slug: 'castor-bay',
    name: 'Castor Bay',
    blurb:
      "Castor Bay's clifftop and beachside homes between Milford and Mairangi Bay take a beating from the coastal weather. We rekey after moving, swap out weather-worn locks and fit home security upgrades, with common hardware on board.",
    homes:
      'Clifftop and beachside homes, many rebuilt or renovated, with large glass sliders and exposed front doors. Weather-rated hardware lasts longer here.',
    jobs: [
      'Weather-worn locks replaced',
      'Sliding door security',
      'Rekeys after a move',
    ],
    nearby: ['milford', 'mairangi-bay', 'forrest-hill'],
    services: ['lock-repair-replacement', 'sliding-door-locks', 'lock-rekeying', 'home-security-upgrades'],
  },
  {
    slug: 'campbells-bay',
    name: 'Campbells Bay',
    blurb:
      "Campbells Bay is leafy coastal residential above Kennedy Park in the East Coast Bays. We handle lockouts, rekeys and lock repairs, and fit deadlocks, window locks and smart hardware for the area's family homes.",
    homes:
      'Leafy streets of family homes from the 1960s onwards around Centennial Park, many with ranchsliders onto decks and bush sections behind.',
    jobs: [
      'Ranchslider and window lock upgrades',
      'Deadbolt fitting',
      'Lockouts and rekeys',
    ],
    nearby: ['mairangi-bay', 'castor-bay', 'sunnynook'],
    services: ['sliding-door-locks', 'deadbolt-installation', 'home-security-upgrades', 'lock-rekeying'],
  },
  {
    slug: 'greenhithe',
    name: 'Greenhithe',
    isBase: true,
    blurb:
      "Greenhithe is home: Shore Doors Locksmiths is based here on the Upper Harbour. It's a suburb of lifestyle blocks and newer subdivisions on larger sections, often with sheds, gates and outbuildings to secure as well as the house. We rekey, upgrade locks and fit home security across the whole property, and look after the local businesses too.",
    homes:
      'Many Greenhithe homes date from the 1990s and 2000s on bigger sections, with sheds, gates and outbuildings as well as the house. Keying the whole property alike, so one key does the house, shed and gate, is a popular job.',
    jobs: [
      'Keying house, shed and gate locks alike',
      'Gate latches and padlocks for lifestyle blocks',
      'Digital lock and deadbolt upgrades',
    ],
    nearby: ['albany', 'glenfield', 'beach-haven'],
    services: ['lock-rekeying', 'key-cutting', 'smart-lock-installation', 'deadbolt-installation'],
  },
];

module.exports = { serviceAreas };
